import type { RepeatUnit, TaskScheduleConditionalType } from '@/model/Task.model';

const DAY_SUFFIX_PATTERN = /^(-?\d+)d$/i;
const HOUR_SUFFIX_PATTERN = /^(-?\d+)h$/i;

/**
 * Parses a single pasted or typed tag into an integer day count for conditional task schedules.
 * Supports plain numbers, day suffix (`d`), and hour suffix (`h` converted to whole days).
 */
export const parseDurationTagValue = (token: string): number | null => {
  const trimmedToken = token.trim();
  if (trimmedToken === '') {
    return null;
  }

  const dayMatch = trimmedToken.match(DAY_SUFFIX_PATTERN);
  if (dayMatch !== null) {
    const dayValue = Number.parseInt(dayMatch[1], 10);
    if (Number.isFinite(dayValue)) {
      return dayValue;
    }
    return null;
  }

  const hourMatch = trimmedToken.match(HOUR_SUFFIX_PATTERN);
  if (hourMatch !== null) {
    const hours = Number.parseInt(hourMatch[1], 10);
    if (!Number.isFinite(hours)) {
      return null;
    }
    if (Math.abs(hours) < 24) {
      return 0;
    }
    return Math.trunc(hours / 24);
  }

  const numberValue = Number(trimmedToken);
  if (Number.isFinite(numberValue)) {
    return numberValue;
  }

  return null;
};

export const buildTags = (tags: Array<string>): Array<number> => {
  const processedTags = tags.reduce((uniqueValues: Set<number>, tag: string) => {
    tag.split(',').forEach((item) => {
      const parsedValue = parseDurationTagValue(item);
      if (parsedValue !== null) {
        uniqueValues.add(parsedValue);
      }
    });

    return uniqueValues;
  }, new Set<number>());

  return Array.from(processedTags);
};

export const RECURRING_PATTERN_MAX_DAYS = 365;

export interface RecurringPatternInputs {
  from: number | null;
  to: number | null;
  every: number | null;
  everyUnit: RepeatUnit;
  type: TaskScheduleConditionalType | undefined;
}

export const generateRecurringPattern = ({
  from,
  to,
  every,
  everyUnit,
  type,
}: RecurringPatternInputs): Array<number> => {
  if (from === null || to === null || every === null) {
    return [];
  }
  if (!Number.isInteger(from) || !Number.isInteger(to) || !Number.isInteger(every)) {
    return [];
  }
  if (every <= 0 || from > to) {
    return [];
  }

  const stepDays = everyUnit === 'weeks' ? every * 7 : every;
  const days: Array<number> = [];
  for (let day = from; day <= to; day += stepDays) {
    if (type === 'study_day' && day === 0) {
      continue;
    }
    days.push(day);
    if (days.length >= RECURRING_PATTERN_MAX_DAYS) {
      break;
    }
  }
  return days;
};

export const countRecurringPattern = ({ from, to, every, everyUnit, type }: RecurringPatternInputs): number => {
  if (from === null || to === null || every === null) {
    return 0;
  }
  if (!Number.isInteger(from) || !Number.isInteger(to) || !Number.isInteger(every)) {
    return 0;
  }
  if (every <= 0 || from > to) {
    return 0;
  }

  const stepDays = everyUnit === 'weeks' ? every * 7 : every;
  const stepCount = Math.floor((to - from) / stepDays) + 1;
  if (type === 'study_day') {
    const includesZero = from <= 0 && to >= 0 && (0 - from) % stepDays === 0;
    return includesZero ? stepCount - 1 : stepCount;
  }
  return stepCount;
};
